import { resolveRatesFromPosition } from '../../utils/resolveRatesFromPosition';

export { resolveRatesFromPosition };

export function mapFindResultToPayrollEntry(record, generatedId, header) {
  return {
    customPayrollId: record.customPayrollId ?? null,
    payRate: record.payRate ?? record.position?.pay ?? null,
    billRate: record.billRate ?? record.position?.bill ?? null,
    hoursPay: record.hoursPay ?? null,
    amountPay: record.amountPay ?? null,
    hoursBill: record.hoursBill ?? null,
    amountBill: record.amountBill ?? null,
    sepCheck: record.sepCheck ?? false,
    sepInvoice: record.sepInvoice ?? false,
    comments: record.comments ?? null,
    payCode: record.payCode ?? null,
    billingCode: record.billingCode ?? null,
    department: record.department?.departmentId ? { departmentId: record.department.departmentId, name: record.department.name } : null,
    position: record.position?.positionId ? { positionId: record.position.positionId, name: record.position.name } : null,
    person: record.person?.personId ? { personId: record.person.personId } : null,
    personId: record.person?.personId ?? null,
    employeeId: record.person?.employeeId ?? null,
    id: generatedId,
    _idHeader: header ? header._idHeader : null
  };
}

export function mapFindResultToPositionValue(record, company) {
  const { department, position, payRate, billRate } = record || {};

  if (!position?.positionId) {
    return null;
  }

  const positionInput = {
    pay: payRate ?? position.pay,
    bill: billRate ?? position.bill,
    markUp: position.markUp
  };
  const resolvedRates = resolveRatesFromPosition(positionInput, company);

  return {
    name: department?.name && position?.name ? `${department.name} - ${position.name}` : position.name,
    positionId: position.positionId,
    positionName: position.name,
    departmentId: department?.departmentId,
    departmentName: department?.name,
    pay: position.pay ?? resolvedRates.pay,
    bill: position.bill ?? resolvedRates.bill,
    markUp: position.markUp,
    payRate: payRate != null && payRate !== '' ? String(payRate) : resolvedRates.payRate,
    billRate: billRate != null && billRate !== '' ? String(billRate) : resolvedRates.billRate
  };
}

export function mapDepartmentAndPositionList(departments = [], options = {}) {
  const { composedLabelKey = 'name', includeBill = true, includeOtMarkUp = false, includeDepartmentName = true } = options;

  const positionAndDepartmentList = [];

  departments.forEach((department) => {
    if (department.positions) {
      department.positions.forEach((position) => {
        const composedLabel = `${department.name} - ${position.name}`;
        const entry = {
          positionId: position.positionId,
          pay: position.pay,
          markUp: position.markUp,
          departmentId: department.departmentId
        };

        if (composedLabelKey === 'positionName') {
          entry.positionName = composedLabel;
        } else {
          entry.name = composedLabel;
          entry.positionName = position.name;
        }

        if (includeBill) {
          entry.bill = position.bill;
        }
        if (includeOtMarkUp) {
          entry.otMarkUp = position.otMarkUp;
        }
        if (includeDepartmentName) {
          entry.departmentName = department.name;
        }

        positionAndDepartmentList.push(entry);
      });
    }
  });

  return positionAndDepartmentList;
}

export function hasRateValue(value) {
  return value != null && value !== '';
}

export function hasPositiveValue(value) {
  if (value == null || value === '') return false;
  const num = parseFloat(value);
  return Number.isFinite(num) && num > 0;
}

export function employeeMissingPosition(employee) {
  return !employee?.position?.positionId;
}

export function employeeMissingPositionRates(employee) {
  if (employeeMissingPosition(employee)) return false;
  return !hasRateValue(employee.rate) || !hasRateValue(employee.bill);
}

export function employeeHasDepartmentPositionError(employeeId, errors = []) {
  return errors.some((error) => error.id === employeeId && error.departmentPosition);
}

export function isDepartmentPositionInvalid(employee, errors = []) {
  const missingPosition = employee.isNewEmployee && employeeMissingPosition(employee);
  const missingRatesFromPosition = employeeMissingPositionRates(employee);
  const missingPositionWithData = employeeHasDepartmentPositionError(employee.id, errors);

  return missingPosition || missingRatesFromPosition || missingPositionWithData;
}

export function resolvePositionDropdownValue(payroll, positionList = []) {
  if (!payroll?.position?.positionId) {
    return payroll?.position ?? null;
  }

  const matched = positionList.find((p) => Number(p.positionId) === Number(payroll.position.positionId));
  if (matched) return matched;

  if (payroll.department?.name && payroll.position?.name) {
    return {
      ...payroll.position,
      name: `${payroll.department.name} - ${payroll.position.name}`,
      positionName: payroll.position.name,
      departmentId: payroll.department.departmentId,
      departmentName: payroll.department.name
    };
  }

  return payroll.position;
}

export function resolvePositionRatesAndRefs(value) {
  const payRate = value.payRate != null && value.payRate !== '' ? value.payRate : value.pay != null ? value.pay : null;
  const billRate = value.billRate != null && value.billRate !== '' ? value.billRate : value.bill != null ? value.bill : null;
  const department =
    value?.departmentId != null
      ? {
          departmentId: value.departmentId,
          name: value.departmentName
        }
      : null;
  const position =
    value?.positionId != null
      ? {
          positionId: value.positionId,
          name: value.positionName
        }
      : null;

  return { payRate, billRate, department, position };
}

export function getEmployeeMissingFieldLabels(employee) {
  const missing = [];

  if (employeeMissingPosition(employee)) {
    missing.push('Department-Position');
    return missing;
  }

  if (!hasRateValue(employee.rate)) {
    missing.push('Pay Rate');
  }

  if (!hasRateValue(employee.bill)) {
    missing.push('Bill Rate');
  }

  return missing;
}
